import { useMemo, useCallback } from 'react';

export const useMatrixCalculations = (
    effectiveRR,
    risk,
    accountBalance,
    totalTrades,
    currentWinRate,
    maxDailyLosses
) => {
    const calculateBreakEven = useMemo(() => {
        if (effectiveRR === '--' || !effectiveRR) return '50.00';
        return (100 / (1 + effectiveRR.toNumber())).toFixed(2);
    }, [effectiveRR]);

    const generateWinRates = useMemo(() => {
        const breakEvenRate = parseFloat(calculateBreakEven);
        const rates = [breakEvenRate];
        for (let rate = 30; rate <= 95; rate += 5) {
            if (!rates.includes(rate)) {
                rates.push(rate);
            }
        }
        return rates.sort((a, b) => a - b);
    }, [calculateBreakEven]);

    const calculateStats = useCallback((winRate) => {
        const trades = totalTrades;
        const wins = Math.round(trades * (winRate / 100));
        const losses = trades - wins;

        const maxLossStreak = Math.ceil(Math.log(100) / Math.log(1 / (1 - winRate / 100)));
        const maxWinStreak = Math.ceil(Math.log(100) / Math.log(1 / (winRate / 100)));

        const maxLossDays = Math.ceil(maxLossStreak / maxDailyLosses);

        const maxDrawdown = maxLossStreak * (risk === '--' ? 0 : risk.toNumber());
        const drawdownPercent = (maxDrawdown / (accountBalance === '--' ? 1 : accountBalance.toNumber())) * 100;

        const rrValue = effectiveRR === '--' ? 0 : effectiveRR.toNumber();
        const totalR = wins * rrValue - losses;
        const expectancy = totalR / trades;

        const grossProfit = wins * rrValue * (risk === '--' ? 0 : risk.toNumber());
        const grossLoss = losses * (risk === '--' ? 0 : risk.toNumber());
        const profitFactor = grossLoss === 0 ? 0 : grossProfit / grossLoss;

        const totalMoney = totalR * (risk === '--' ? 0 : risk.toNumber());
        const profitPercent = (totalMoney / (accountBalance === '--' ? 1 : accountBalance.toNumber())) * 100;

        return {
            winRate,
            wins,
            losses,
            maxLossStreak,
            maxWinStreak,
            maxLossDays,
            maxDrawdown,
            drawdownPercent: drawdownPercent.toFixed(1),
            profitPercent: profitPercent.toFixed(1),
            expectancy: expectancy.toFixed(2),
            totalR: totalR.toFixed(1),
            totalMoney,
            profitFactor: profitFactor.toFixed(2)
        };
    }, [totalTrades, maxDailyLosses, risk, accountBalance, effectiveRR]);

    const currentStats = useMemo(() =>
        calculateStats(currentWinRate),
        [calculateStats, currentWinRate]
    );

    return {
        breakEvenRate: parseFloat(calculateBreakEven),
        winRates: generateWinRates,
        currentStats,
        calculateStats
    };
};